import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Box,
  InputAdornment,
  Typography,
  Alert,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { Delete, Add, Check, Send } from '@mui/icons-material';
import { submitAdvanceLoan, submitBatchAdvanceLoans } from './api';
import apiService from '../Transactions/api'; // Use the same API service as CollectCash

const ApplicantForm = ({
  index,
  formData,
  handleFormChange,
  handleRemoveApplicant,
  loading,
  errorMessage,
  success,
  members,
  loadingMembers
}) => {
  // Calculate the maximum advance amount (could be based on user contribution history)
  const calculateMaxAdvance = (selectedMember) => {
    if (!selectedMember) return 0;
    // Fixed maximum advance of 20,000
    return 20000;
  };

  const maxAdvanceAmount = calculateMaxAdvance(formData.selectedMember);

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant="h6" gutterBottom>
          Applicant {index + 1}
        </Typography>
        {index > 0 && (
          <IconButton
            color="error"
            onClick={() => handleRemoveApplicant(index)}
            size="small"
          >
            <Delete />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={3}>
        {success && (
          <Grid item xs={12}>
            <Alert severity="success">
              Advance request confirmed successfully!
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Autocomplete
            options={Array.isArray(members) ? members : []}
            getOptionLabel={(option) => `${option.name} (${option.member_id})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Member"
                required
                fullWidth
                error={!formData.member_id && errorMessage !== ''}
                helperText={!formData.member_id && errorMessage !== '' ? 'Please select a member' : ''}
              />
            )}
            onChange={(event, newValue) => {
              handleFormChange(index, 'selectedMember', newValue);
              handleFormChange(index, 'member_id', newValue ? newValue.id : '');
            }}
            value={formData.selectedMember}
            loading={loadingMembers}
            loadingText="Loading members..."
          />
        </Grid>

        {formData.selectedMember && (
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="body2">
                Based on contribution history, maximum advance eligible: <strong>KES {maxAdvanceAmount.toFixed(2)}</strong>
              </Typography>
            </Box>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Advance Amount"
            type="number"
            required
            value={formData.amount}
            onChange={(e) => handleFormChange(index, 'amount', parseFloat(e.target.value) || 0)}
            InputProps={{
              startAdornment: <InputAdornment position="start">KES</InputAdornment>,
            }}
            error={(formData.amount <= 0 || formData.amount > maxAdvanceAmount) && errorMessage !== ''}
            helperText={
              formData.amount <= 0 && errorMessage !== ''
                ? 'Valid amount is required'
                : formData.amount > maxAdvanceAmount && errorMessage !== ''
                  ? 'Amount exceeds maximum eligible advance'
                  : ''
            }
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Officer Name"
            required
            value={formData.officer_name}
            onChange={(e) => handleFormChange(index, 'officer_name', e.target.value)}
            error={!formData.officer_name && errorMessage !== ''}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Reason for Advance"
            multiline
            rows={3}
            required
            value={formData.notes}
            onChange={(e) => handleFormChange(index, 'notes', e.target.value)}
            error={!formData.notes && errorMessage !== ''}
            helperText={!formData.notes && errorMessage !== '' ? 'Please provide a reason for this advance' : ''}
          />
        </Grid>

        {/* Error Message */}
        {errorMessage && (
          <Grid item xs={12}>
            <Alert severity="error">
              {errorMessage}
            </Alert>
          </Grid>
        )}

        {loading && (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

const AdvanceForm = ({ onClose, selectedMemberId = null }) => {
  const [applicantCount, setApplicantCount] = useState(1);
  const [formLoading, setFormLoading] = useState(false);
  const [batchSuccess, setBatchSuccess] = useState(false);
  const [batchError, setBatchError] = useState('');
  const [individualLoading, setIndividualLoading] = useState(Array(1).fill(false));
  const [errorMessages, setErrorMessages] = useState(Array(1).fill(''));
  const [successStates, setSuccessStates] = useState(Array(1).fill(false));
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Initial form state for multiple applicants
  const initialFormState = {
    member_id: selectedMemberId || '',
    selectedMember: null, // Store the full member object for display
    amount: 0,
    officer_name: '',
    notes: '',
    loan_type: 'advance'
  };

  const [advanceForms, setAdvanceForms] = useState([{...initialFormState}]);

  // Fetch all members when component mounts - same pattern as CollectCash
  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      const data = await apiService.fetchMembers();
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching members:", err);
      setBatchError("Failed to load members: " + err.message);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Handle adding more applicant forms
  const handleAddApplicant = () => {
    setApplicantCount(applicantCount + 1);
    setAdvanceForms([...advanceForms, {...initialFormState}]);
    setIndividualLoading([...individualLoading, false]);
    setErrorMessages([...errorMessages, '']);
    setSuccessStates([...successStates, false]);
  };

  // Handle removing an applicant form
  const handleRemoveApplicant = (index) => {
    if (advanceForms.length > 1) {
      const newForms = [...advanceForms];
      newForms.splice(index, 1);
      setAdvanceForms(newForms);

      const newLoading = [...individualLoading];
      newLoading.splice(index, 1);
      setIndividualLoading(newLoading);

      const newErrorMessages = [...errorMessages];
      newErrorMessages.splice(index, 1);
      setErrorMessages(newErrorMessages);

      const newSuccessStates = [...successStates];
      newSuccessStates.splice(index, 1);
      setSuccessStates(newSuccessStates);

      setApplicantCount(applicantCount - 1);
    }
  };

  // Handle form field changes
  const handleFormChange = (index, field, value) => {
    const newForms = [...advanceForms];
    newForms[index] = {
      ...newForms[index],
      [field]: value
    };
    setAdvanceForms(newForms);
  };

  // Calculate the maximum advance amount
  const calculateMaxAdvance = (memberId) => {
    // Since we don't have member data loaded here, we'll use a fixed amount
    // In a real scenario, you might want to fetch this data when needed
    return 20000;
  };

  // Validate a single form
  const validateForm = (formData, index) => {
    if (!formData.member_id) {
      return 'Please select a member';
    }

    if (formData.amount <= 0) {
      return 'Please enter a valid amount';
    }

    if (!formData.officer_name) {
      return 'Please enter officer name';
    }

    if (!formData.notes) {
      return 'Please provide a reason for this advance';
    }

    // Check if advance amount is within limits
    const maxAdvance = calculateMaxAdvance(formData.member_id);
    if (formData.amount > maxAdvance) {
      return `Maximum advance amount for this member is KES ${maxAdvance.toFixed(2)}`;
    }

    return '';
  };

  // Handle form confirmation for each applicant individually
  const handleIndividualConfirm = async (index) => {
    try {
      // Update loading state for this specific form
      const newLoading = [...individualLoading];
      newLoading[index] = true;
      setIndividualLoading(newLoading);

      // Reset error message for this form
      const newErrorMessages = [...errorMessages];
      newErrorMessages[index] = '';
      setErrorMessages(newErrorMessages);

      const formData = advanceForms[index];

      // Form validation
      const validationError = validateForm(formData, index);
      if (validationError) {
        newErrorMessages[index] = validationError;
        setErrorMessages(newErrorMessages);
        newLoading[index] = false;
        setIndividualLoading(newLoading);
        return;
      }

      // Create the submission data
      const formDataToSubmit = {
        principal_amount: formData.amount,
        officer_name: formData.officer_name,
        notes: formData.notes,
        loan_type: 'advance',
        member: formData.member_id
      };

      console.log('Formatted dataToSubmit for individual:', formDataToSubmit);

      // Submit the data
      await submitAdvanceLoan(formDataToSubmit);

      // Update success state for this specific form
      const newSuccessStates = [...successStates];
      newSuccessStates[index] = true;
      setSuccessStates(newSuccessStates);

      // Reset this form
      const newForms = [...advanceForms];
      newForms[index] = {...initialFormState};
      setAdvanceForms(newForms);

    } catch (error) {
      console.error('Advance submission failed:', error);

      // Update error message for this specific form
      const newErrorMessages = [...errorMessages];
      newErrorMessages[index] = error.response?.data?.message || 'Failed to submit advance request';
      setErrorMessages(newErrorMessages);

    } finally {
      // Update loading state for this specific form
      const newLoading = [...individualLoading];
      newLoading[index] = false;
      setIndividualLoading(newLoading);
    }
  };

  // Handle batch submission of all forms
  const handleBatchSubmit = async () => {
    try {
      setBatchError('');
      setFormLoading(true);

      // Validate all forms first
      const validationErrors = [];
      advanceForms.forEach((form, index) => {
        const error = validateForm(form, index);
        if (error) {
          validationErrors.push(`Applicant ${index + 1}: ${error}`);
        }
      });

      if (validationErrors.length > 0) {
        setBatchError(`Please fix the following errors:\n${validationErrors.join('\n')}`);
        return;
      }

      // Submit all forms as a batch
      const response = await submitBatchAdvanceLoans(advanceForms);

      if (response.results) {
        // Check if all submissions were successful
        const allSuccessful = response.results.every(result => result.success);

        if (allSuccessful) {
          setBatchSuccess(true);

          // Reset all forms
          const emptyForms = Array(applicantCount).fill(null).map(() => ({...initialFormState}));
          setAdvanceForms(emptyForms);

        } else {
          // Update individual error messages for failed submissions
          const newErrorMessages = [...errorMessages];
          const newSuccessStates = [...successStates];

          response.results.forEach((result, index) => {
            if (!result.success && index < newErrorMessages.length) {
              newErrorMessages[index] = result.error || 'Failed to submit advance request';
            } else if (result.success && index < newSuccessStates.length) {
              newSuccessStates[index] = true;
            }
          });

          setErrorMessages(newErrorMessages);
          setSuccessStates(newSuccessStates);

          setBatchError(`Some submissions failed. ${response.success_count} succeeded, ${response.failure_count} failed.`);
        }
      }
    } catch (error) {
      console.error('Batch advance submission failed:', error);
      setBatchError(error.response?.data?.message || 'Failed to submit batch advance request');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Number of Applicants: {applicantCount}
            </Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddApplicant}
                sx={{ mr: 1 }}
              >
                Add Applicant
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {batchSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          All advance requests submitted successfully!
        </Alert>
      )}

      {batchError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {batchError}
        </Alert>
      )}

      {/* Render multiple applicant forms */}
      {advanceForms.map((formData, index) => (
        <ApplicantForm
          key={index}
          index={index}
          formData={formData}
          handleFormChange={handleFormChange}
          handleRemoveApplicant={handleRemoveApplicant}
          loading={individualLoading[index]}
          errorMessage={errorMessages[index]}
          success={successStates[index]}
          members={members}
          loadingMembers={loadingMembers}
        />
      ))}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>

        <Box>
          {applicantCount > 1 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Send />}
              onClick={handleBatchSubmit}
              disabled={formLoading}
              sx={{ mr: 2 }}
            >
              {formLoading ? 'Processing...' : 'Submit All Applications'}
            </Button>
          )}

          <Button
            variant="contained"
            color="success"
            onClick={() => handleIndividualConfirm(0)}
            disabled={individualLoading[0]}
            startIcon={<Check />}
          >
            {individualLoading[0] ? 'Processing...' : applicantCount > 1 ? 'Submit First Application' : 'Submit Application'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AdvanceForm;